/**
 * Clients browse view - one row per LS customer. The Issues column reads
 * 0 everywhere today (the only client-domain exception keys Skimmer
 * customer IDs, not LS ids); the join lights up when an LS-keyed client
 * exception ships.
 */
import React from 'react'
import { Chip } from '@mui/material'
import { statusPillChipProps } from '../../components/shared/AppDrawer/chipPresets'
import { formatBusinessDate, formatCurrency } from '../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../exceptions/shared/issuesColumn'
import {
  composeRowSubtitle,
  textPart,
} from '../exceptions/shared/rowSubtitle'
import type {
  ColumnGroupDef,
  ExceptionColumnDef,
  ExceptionFilterDef,
} from '../exceptions/types'
import type { DataExplorerViewConfig } from './types'
import { buildHasIssuesParam, hasIssuesFilter, withDetectedAge } from './shared'
import inspectorApi from '../../services/inspector/api'

const STATUS_FILTER_KEY = 'status'
const LOYALTY_FILTER_KEY = 'loyalty'

const renderYesNo = (value: any): React.ReactNode =>
  React.createElement(
    Chip,
    statusPillChipProps(value ? 'Yes' : 'No', value ? 'success' : 'neutral'),
  )

const renderClientStatus = (value: any): React.ReactNode =>
  React.createElement(
    Chip,
    statusPillChipProps(
      value === 'active' ? 'Active' : 'Deleted',
      value === 'active' ? 'success' : 'warning',
    ),
  )

// Group bands (second header row). Frozen identity columns lead; the rest
// split into contact details, account state, and purchase activity.
const clientsGroups: ColumnGroupDef[] = [
  { id: 'client', label: 'Client', separatorAfter: true },
  { id: 'details', label: 'Details', separatorAfter: true },
  { id: 'account', label: 'Account', separatorAfter: true },
  { id: 'activity', label: 'Activity' },
]

const columns: ExceptionColumnDef[] = [
  withDetectedAge({ ...buildIssuesColumn(), freeze: 'left', group: 'client' }),
  {
    key: 'name',
    label: 'Name',
    freeze: 'left',
    group: 'client',
    align: 'left',
    width: 200,
    minWidth: 200,
  },
  { key: 'company_name', label: 'Company', group: 'details', align: 'left', render: (v) => v || '-' },
  { key: 'customer_code', label: 'Code', group: 'details', align: 'left' },
  { key: 'email', label: 'Email', group: 'details', align: 'left', render: (v) => v || '-' },
  { key: 'phone', label: 'Phone', group: 'details', align: 'left', render: (v) => v || '-' },
  {
    key: 'store_credit_balance',
    label: 'Store\nCredit',
    align: 'right',
    type: 'currency',
    group: 'account',
    render: (value) => formatCurrency(value),
    exportFormat: 'currency',
  },
  {
    key: 'loyalty_enabled',
    label: 'Loyalty',
    align: 'center',
    group: 'account',
    render: (value) => renderYesNo(value),
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    group: 'account',
    render: (value) => renderClientStatus(value),
  },
  {
    key: 'total_purchases',
    label: 'Purchases\n(all time)',
    align: 'right',
    type: 'currency',
    group: 'activity',
    render: (value) => formatCurrency(value),
    exportFormat: 'currency',
  },
  {
    key: 'last_purchase_date',
    label: 'Last\nPurchase',
    align: 'right',
    group: 'activity',
    render: (value) => (value ? formatBusinessDate(value) : '-'),
    exportFormat: 'date',
  },
]

const filters: ExceptionFilterDef[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Name, email, code or phone',
  },
  {
    key: STATUS_FILTER_KEY,
    type: 'select',
    label: 'Status',
    defaultValue: 'active',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'deleted', label: 'Deleted' },
      { value: 'all', label: 'All' },
    ],
  },
  {
    key: LOYALTY_FILTER_KEY,
    type: 'select',
    label: 'Loyalty',
    defaultValue: 'all',
    options: [
      { value: 'all', label: 'All' },
      { value: 'yes', label: 'Loyalty on' },
      { value: 'no', label: 'Loyalty off' },
    ],
  },
  hasIssuesFilter,
]

export const clientsViewConfig: DataExplorerViewConfig = {
  viewKey: 'clients',
  codes: [],
  fpUserAccessId: 'clients',
  pageHeader: {
    title: 'Clients',
    description: 'Browse all Lightspeed customers for this franchise.',
    breadcrumb: 'Clients',
  },
  complexity: 'simple',
  rowKeyField: 'object_id',
  defaultSortColumn: 'name',
  defaultSortDirection: 'asc',
  serverTable: { defaultPageSize: 50, pageSizeOptions: [50, 100, 200], enableSearch: true },
  serverSort: {
    sortableKeys: ['name', 'store_credit_balance', 'errors'],
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
  },
  columns,
  columnGroups: clientsGroups,
  disableBulkSelect: true,
  filters,
  buildFetchParams: (urlState) => ({
    ...buildHasIssuesParam(urlState.filterValues),
    ...(urlState.filterValues?.[STATUS_FILTER_KEY] &&
    urlState.filterValues[STATUS_FILTER_KEY] !== 'active'
      ? { status: urlState.filterValues[STATUS_FILTER_KEY] }
      : {}),
    ...(urlState.filterValues?.[LOYALTY_FILTER_KEY] &&
    urlState.filterValues[LOYALTY_FILTER_KEY] !== 'all'
      ? { loyalty: urlState.filterValues[LOYALTY_FILTER_KEY] }
      : {}),
  }),
  getRowTitle: (row) => String(row.name || row.customer_code || 'Client'),
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.customer_code),
      textPart(row.company_name),
      textPart(row.email),
    ]),
  fetchPage: (fp, params) => inspectorApi.getDataExplorerClients(fp, params),
}
